import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import memoize from 'memoize-one';
import { Button, Icons } from 'sarsaparilla';
import { debounce } from 'lodash';
import { STATE_UNLOADED } from '../../../../ui-lottery/src/utils/async_utils';
import { fetchOrganizations } from '../../actions/organization';
import { fetchActivities } from '../../actions/activity';
import { saveSearch } from '../../actions/search';
import { ActivityProps } from '../../props/activity';
import { OrganizationProps } from '../../props/organization';
import { SearchProps } from '../../props/search';
import { getOrganizations } from '../../reducers/organizations';
import { getActivities } from '../../reducers/activities';
import { getState } from '../../reducers';
import { TextInput, InlineCheckboxInput } from '../../../../ui-lottery/src/components/inputs';
import { states } from '../../../../ui-navigation/src/utils/stateHelper';
import { getSearch } from '../../reducers/search';
import { flattenOrgs, orgsToOptions } from '../../utils/organization';

import styles from './SearchForm.scss';

const propTypes = {
  searchQuery: SearchProps,
  organizations: PropTypes.arrayOf(OrganizationProps),
  activities: PropTypes.arrayOf(ActivityProps),
  states: PropTypes.objectOf(PropTypes.number),
  saveSearch: PropTypes.func.isRequired,
  onSearch: PropTypes.func,
  inline: PropTypes.bool,
  showLimitSelect: PropTypes.bool,
  searchOnChange: PropTypes.bool
};

const defaultProps = {
  inline: false,
  showLimitSelect: false,
  searchOnChange: false
};

export class SearchForm extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      query: this.props.searchQuery,
      orgId: ''
    };

    this.searchAssetType = this.searchAssetType.bind(this);
    this.setOrgs = this.setOrgs.bind(this);
    this.setTerms = this.setTerms.bind(this);
    this.setStateCode = this.setStateCode.bind(this);
    this.setActivity = this.setActivity.bind(this);
    this.setLimit = this.setLimit.bind(this);

    this.searchOnChange = debounce(this.searchOnChange.bind(this), 350);
    this.goSearch = this.goSearch.bind(this);
  }

  componentDidMount() {
    if (this.props.states.organizations === STATE_UNLOADED) {
      this.props.fetchOrganizations();
    }
    if (this.props.states.activities === STATE_UNLOADED) {
      this.props.fetchActivities({ sort: 'name' });
    }
  }

  getOrgOptions = memoize((organizations) => {
    if (organizations) {
      return orgsToOptions(flattenOrgs(organizations));
    }

    return [];
  });

  componentDidUpdate(prevProps) {
    if (prevProps.searchQuery !== this.props.searchQuery && this.props.searchQuery !== this.state.query) {
      //TODO: this should really not be in state
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ query: this.props.searchQuery });
    }
  }

  setStateCode(event) {
    let { value } = event.target;

    if (value === '') {
      value = null;
    }

    this.setState((prevState) => ({ ...prevState, query: { ...prevState.query, state: value } }), this.searchOnChange);
  }

  setActivity(event) {
    let { value } = event.target;

    if (value === '') {
      value = null;
    }

    this.setState((prevState) => ({
      ...prevState,
      query: { ...prevState.query, activity: value }
    }), this.searchOnChange);
  }

  setOrgs(event) {
    const { value } = event.target;

    if (value === '') {
      this.setState((prevState) => ({
        ...prevState,
        orgId: value,
        query: { ...prevState.query, orgIds: null }
      }), this.searchOnChange);
      return;
    }

    this.setState((prevState) => ({
      ...prevState,
      orgId: value,
      query: { ...prevState.query, orgIds: [value] }
    }), this.searchOnChange);
  }

  setTerms(terms) {
    this.setState((prevState) => ({ ...prevState, query: { ...prevState.query, terms } }), this.searchOnChange);
  }

  searchAssetType(event) {
    const { value, checked } = event.target;

    this.setState((prevState) => {
      const { assetTypes } = prevState.query;

      if (checked) {
        return { ...prevState, query: { ...prevState.query, assetTypes: [...assetTypes, value] } };
      }
      return { ...prevState, query: { ...prevState.query, assetTypes: assetTypes.filter((v) => v !== value) } };
    }, this.searchOnChange);
  }

  setLimit(event) {
    const { value } = event.target;
    const limit = Math.min(50, Math.max(10, parseInt(value, 10)));

    this.setState((prevState) => ({ ...prevState, query: { ...prevState.query, limit } }), this.searchOnChange);
  }

  searchOnChange() {
    if (this.props.searchOnChange) {
      this.goSearch();
    }
  }

  goSearch() {
    const after = this.props.onSearch ? this.props.onSearch : () => {};

    this.props.saveSearch(this.state.query)
      .then(after);
  }

  render() {
    const { activities, inline, showLimitSelect, searchOnChange, organizations } = this.props;
    const { query: { assetTypes, state, activity, terms, limit }, orgId} = this.state;
    const orgOptions = this.getOrgOptions(organizations);

    const inlinedRow = !inline ? `${styles.row} ${styles.stacked}` : styles.row;

    return (
      <div role="form" className={styles['search-form']}>
        <div className={styles.row}>
          <div className={styles.cell}>
            <InlineCheckboxInput
              id="search-asset-recareas"
              value="recarea"
              label="Search Recreation Areas"
              defaultChecked={assetTypes.indexOf('recarea') !== -1}
              onChange={this.searchAssetType} />
          </div>
          <div className={styles.cell}>
            <InlineCheckboxInput
              id="search-asset-facility"
              value="facility"
              label="Search Facilities"
              defaultChecked={assetTypes.indexOf('facility') !== -1}
              onChange={this.searchAssetType} />
          </div>
          {showLimitSelect ? (<div className={`${styles['align-right']} ${styles.cell} ${styles.biggest}`}>
            <label htmlFor="records-per-page" className="rec-sr-only">Records Per Page</label>
            <select id="records-per-page" name="records-per-page" value={limit} className={styles['auto-width']} onChange={this.setLimit}>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="40">40</option>
              <option value="50">50</option>
            </select>
            &nbsp; records per page
          </div>) : null}
        </div>
        <div className={inlinedRow}>
          <div className={styles.cell}>
            <label htmlFor="search-states" className="rec-sr-only">Select State</label>
            <select id="search-states" name="search-states" value={state || ''} onChange={this.setStateCode}>
              <option value="">Select State</option>
              {states.map(({ abbreviation: value, label }) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <div className={styles.cell}>
            <label htmlFor="search-activities" className="rec-sr-only">Select Activity</label>
            <select id="search-activities" name="search-activities" value={activity || ''} onChange={this.setActivity}>
              <option value="">Select Activity</option>
              {activities.map(({ id, name }) => <option key={id} value={id}>{name}</option>)}
            </select>
          </div>
          <div className={`${styles.cell}`}>
            <label htmlFor="search-organizations" className="rec-sr-only">Select Organization</label>
            <select id="search-organizations" name="search-organizations" value={orgId} onChange={this.setOrgs}>
              <option value="">Select Organization</option>
              {orgOptions.map((org) => <option key={org.value} value={org.value}>{org.label}</option>)}
            </select>
          </div>
          <div className={styles.cell}>
            <label htmlFor="search-terms" className="rec-sr-only">Search Text</label>
            <TextInput
              id="search-terms"
              value={terms || ''}
              placeholder="Search Text"
              iconLeft={Icons.IconSearch}
              onChange={this.setTerms} />
          </div>
        </div>
        {searchOnChange ? null : (
          <div className={`${styles.row} ${styles['align-right']}`}>
            <Button onClick={this.goSearch}>Search</Button>
          </div>
        )}
      </div>
    );
  }
}

SearchForm.propTypes = propTypes;
SearchForm.defaultProps = defaultProps;

const mapStateToProps = (state) => ({
  searchQuery: getSearch(state),
  organizations: getOrganizations(state),
  activities: getActivities(state),
  states: getState(state, 'organizations', 'activities')
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  saveSearch,
  fetchOrganizations,
  fetchActivities
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(SearchForm);
